'use client';

import { Box, Menu, MenuItem, Checkbox, ListItemText } from '@mui/material';
import { Toolbar, ColumnsPanelTrigger, ExportCsv, useGridApiContext, GridDensity } from '@mui/x-data-grid-pro';

import { gridColumnVisibilityModelSelector, useGridSelector } from '@mui/x-data-grid';
import { useState } from 'react';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import LayersIcon from '@mui/icons-material/Layers';
import {
  SAMPLE_COLUMN_GROUPS,
  applySampleColumnGroupVisibility,
  getSampleColumnGroupVisibility,
  type SampleColumnGroupDefinition,
} from '@/lib/tables/sampleGridColumnGroups';

export function SampleGridToolbar() {
  const apiRef = useGridApiContext();
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const [densityMenuAnchor, setDensityMenuAnchor] = useState<null | HTMLElement>(null);
  const [groupsMenuAnchor, setGroupsMenuAnchor] = useState<null | HTMLElement>(null);

  const handleDensityClick = (event: React.MouseEvent<HTMLElement>) => {
    setDensityMenuAnchor(event.currentTarget);
  };

  const handleDensityClose = () => {
    setDensityMenuAnchor(null);
  };

  const handleDensitySelect = (density: GridDensity) => {
    apiRef.current.setDensity(density);
    handleDensityClose();
  };

  const handleGroupsClick = (event: React.MouseEvent<HTMLElement>) => {
    setGroupsMenuAnchor(event.currentTarget);
  };

  const handleGroupsClose = () => {
    setGroupsMenuAnchor(null);
  };

  /**
   * Toggles a column group: if every field in the group is visible, hides all; otherwise shows all.
   */
  const handleGroupMenuItemClick = (group: SampleColumnGroupDefinition) => {
    const state = getSampleColumnGroupVisibility(columnVisibilityModel, group.id);
    const nextVisible = state !== 'all';
    const nextModel = applySampleColumnGroupVisibility(columnVisibilityModel, group.id, nextVisible);
    apiRef.current.setColumnVisibilityModel(nextModel);
  };

  return (
    <Toolbar>
      <ColumnsPanelTrigger startIcon={<ViewColumnIcon />}>Columns</ColumnsPanelTrigger>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <LayersIcon sx={{ color: 'primary.main', fontSize: 18 }} />
        <Box
          component="button"
          type="button"
          onClick={handleGroupsClick}
          sx={{
            border: 'none',
            background: 'none',
            color: 'primary.main',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
            padding: '6px 8px',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              borderRadius: 1,
            },
          }}
        >
          Groups
        </Box>
      </Box>

      <Menu
        anchorEl={groupsMenuAnchor}
        open={Boolean(groupsMenuAnchor)}
        onClose={handleGroupsClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        {SAMPLE_COLUMN_GROUPS.map((group) => {
          const vis = getSampleColumnGroupVisibility(columnVisibilityModel, group.id);
          return (
            <MenuItem
              key={group.id}
              dense
              onClick={() => {
                handleGroupMenuItemClick(group);
              }}
            >
              <Checkbox
                checked={vis === 'all'}
                indeterminate={vis === 'some'}
                size="small"
                sx={{ mr: 0.5 }}
                tabIndex={-1}
                disableRipple
              />
              <ListItemText primary={group.label} />
            </MenuItem>
          );
        })}
      </Menu>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <DensityMediumIcon sx={{ color: 'primary.main', fontSize: 18 }} />
        <Box
          component="button"
          type="button"
          onClick={handleDensityClick}
          sx={{
            border: 'none',
            background: 'none',
            color: 'primary.main',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
            padding: '6px 8px',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              borderRadius: 1,
            },
          }}
        >
          Density
        </Box>
      </Box>

      <Menu anchorEl={densityMenuAnchor} open={Boolean(densityMenuAnchor)} onClose={handleDensityClose}>
        <MenuItem onClick={() => handleDensitySelect('compact')}>Compact</MenuItem>
        <MenuItem onClick={() => handleDensitySelect('standard')}>Standard</MenuItem>
        <MenuItem onClick={() => handleDensitySelect('comfortable')}>Comfortable</MenuItem>
      </Menu>

      <ExportCsv
        startIcon={<FileDownloadIcon />}
        options={{
          fileName: 'samples',
          delimiter: ',',
          utf8WithBom: true,
        }}
      >
        Export
      </ExportCsv>
    </Toolbar>
  );
}
